import type { WebAssistantSurface } from '@luxuryescapes/contract-trip'
import { trackClientEvent } from 'analytics/analytics'
import { reportLog } from 'services/userMonitoring'
import { countWhere } from 'lib/array/arrayUtils'
import type { VoicePrompt } from 'tripPlanner/components/Chat/voicePromptTypes'
import type { TripDetail, TripDetailsCheck } from 'tripPlanner/api/tripDetails'
import type { NudgeOption } from 'tripPlanner/pages/chat/ChatLandingPage/tripDetailsNudgeAnswers'
import type { SurfaceFocusOutcome } from 'tripPlanner/assistant/surfaceFocus/surfaceFocusTypes'

const CATEGORY = 'chat_trip_planner'

export type EntryPointType =
  | 'typed_prompt'
  | 'booking_card'
  | 'suggestion_chip'
  | 'recent_trip_card'
  | 'whatsapp_handoff'

function chatSubject(descriptor: string, chatId: number | undefined): string {
  return chatId !== undefined ? `${descriptor}_${chatId}` : descriptor
}

export function trackSessionStart(
  chatId: number | undefined,
  sessionType: 'new' | 'returning',
) {
  trackClientEvent({
    subject: chatSubject(`session_${sessionType}`, chatId),
    action: 'session_start',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackEntryPoint(entryType: EntryPointType) {
  trackClientEvent({
    subject: `entry_${entryType}`,
    action: 'entry_point_selected',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackFirstMessageSent(chatId: number, timeToFirstMessageMs: number) {
  trackClientEvent({
    subject: chatSubject(`first_message_${Math.round(timeToFirstMessageMs)}ms`, chatId),
    action: 'first_message_sent',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackHotelDrawerOpened(chatId: number) {
  trackClientEvent({
    subject: chatSubject('hotel_drawer', chatId),
    action: 'hotel_drawer_opened',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackExperienceDrawerOpened(chatId: number) {
  trackClientEvent({
    subject: chatSubject('experience_drawer', chatId),
    action: 'experience_drawer_opened',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackCruiseDrawerOpened(chatId: number) {
  trackClientEvent({
    subject: chatSubject('cruise_drawer', chatId),
    action: 'cruise_drawer_opened',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackVoiceInputUsed(chatId: number | undefined) {
  trackClientEvent({
    subject: chatSubject('voice_input', chatId),
    action: 'voice_input_used',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackVoicePromptEvent(
  event: 'shown' | 'accepted' | 'dismissed',
  prompt: VoicePrompt,
  chatId: number | undefined,
) {
  reportLog(`trip_chat.voice_prompt_${event}`, { prompt, chatId }, 'info')
}

/** How the trip details nudge was used in a landing draft, so drafts it never asked about aren't compared with nudged ones */
export type NudgeUse =
  | 'answered' // a question was answered, with an answer button or in the traveller's own words
  | 'unanswered' // a question showed and wasn't answered
  | 'dismissed_earlier' // the nudge was dismissed earlier in the session, so it never asks
  | 'not_typed' // only chips were sent, which the nudge doesn't ask about
  | 'too_short' // too few words to check
  | 'unavailable' // checks were failing, so the nudge was off
  | 'not_checked' // sent before a check of the text sent came back, and not as a tapped answer left it
  | 'complete' // no detail was missing
  | 'not_asked' // a detail was missing but no question had shown yet

/** A landing draft as it was sent, and what the nudge did with it */
export interface TripDetailsSubmission {
  /** Details the draft gave, only when the last check was of the text sent, or it's as a tapped answer left it */
  details?: TripDetailsCheck
  /** Questions the traveller saw in this draft */
  shownCount: number
  /** Questions the traveller answered in this draft */
  answeredCount: number
  nudgeUse: NudgeUse
}

/** The trip details a landing draft gave when it was sent, grouped by how the nudge was used */
export function trackTripDetailsSubmitted({ details, shownCount, answeredCount, nudgeUse }: TripDetailsSubmission) {
  reportLog('trip_chat.trip_details_submitted', {
    ...(details && { ...details, detailCount: countWhere(Object.values(details), Boolean) }),
    nudgeShownCount: shownCount,
    nudgeAnsweredCount: answeredCount,
    nudgeUse,
  }, 'info')
}

/** The landing nudge asked about a missing trip detail */
export function trackTripDetailsNudgeShown(detail: TripDetail) {
  reportLog('trip_chat.trip_details_nudge_shown', { detail }, 'info')
}

/** The traveller gave a detail the nudge asked about, with an answer button or in their own words */
export function trackTripDetailsNudgeAnswered(detail: TripDetail, answer: { via: 'answer'; option: NudgeOption } | { via: 'own_words' }) {
  reportLog('trip_chat.trip_details_nudge_answered', { detail, ...answer }, 'info')
}

/** The traveller dismissed the nudge, which turns it off for the session */
export function trackTripDetailsNudgeDismissed(detail: TripDetail) {
  reportLog('trip_chat.trip_details_nudge_dismissed', { detail }, 'info')
}

export function trackMapPinClicked(chatId: number, offerId: string) {
  trackClientEvent({
    subject: chatSubject(`map_pin_${offerId}`, chatId),
    action: 'map_pin_clicked',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackViewModeToggled(chatId: number, newMode: 'detail' | 'compact') {
  trackClientEvent({
    subject: chatSubject(`view_mode_${newMode}`, chatId),
    action: 'view_mode_toggled',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackCarouselPaginated(chatId: number | undefined) {
  trackClientEvent({
    subject: chatSubject('carousel', chatId),
    action: 'carousel_paginated',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackCarouselViewAllClicked(chatId: number | undefined) {
  trackClientEvent({
    subject: chatSubject('hotel_carousel', chatId),
    action: 'carousel_view_all_clicked',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackSessionEnd(chatId: number | undefined, durationMs: number) {
  trackClientEvent({
    subject: chatSubject(`session_end_${Math.round(durationMs)}ms`, chatId),
    action: 'session_end',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export type AssistantSurfaceSnapshotOutcome =
  | 'chunked_resolved'
  | 'chunked_recovery_resolved'
  | 'degraded_timeout'
  | 'degraded_chunk_cache_miss'
  | 'degraded_validation'
  | 'degraded_network'
  | 'degraded_auth'
  | 'degraded_idempotency'

export type AssistantSurfaceSnapshotLatencyBucket =
  | 'under_100ms'
  | '100_to_249ms'
  | '250_to_499ms'
  | '500_to_749ms'
  | 'deadline'

export function assistantSurfaceSnapshotLatencyBucket(elapsedMs: number): AssistantSurfaceSnapshotLatencyBucket {
  if (elapsedMs < 100) return 'under_100ms'
  if (elapsedMs < 250) return '100_to_249ms'
  if (elapsedMs < 500) return '250_to_499ms'
  if (elapsedMs < 750) return '500_to_749ms'
  return 'deadline'
}

export function trackAssistantSurfaceSnapshotOutcome(
  surface: string,
  outcome: AssistantSurfaceSnapshotOutcome,
  latencyBucket: AssistantSurfaceSnapshotLatencyBucket,
) {
  trackClientEvent({
    subject: `${surface}_${outcome}_${latencyBucket}`,
    action: 'assistant_surface_snapshot_resolved',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackSurfaceActivityDropped(count: number) {
  trackClientEvent({
    subject: `trip_planner_activity_dropped_${count}`,
    action: 'surface_activity_accumulator_capped',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackOfferPageFocusExecutorOutcome(outcome: SurfaceFocusOutcome, surface: WebAssistantSurface = 'accommodation_offer_page') {
  trackClientEvent({
    subject: `${surface}_${outcome}`,
    action: 'offer_page_focus_executor_resolved',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}
